import React, { memo, useState } from 'react'
import styled from 'styled-components'

import {
    ChargePoint,
    useUserGetOwnedChargePointsQuery,
    useLazyGreenChargingChargePointStatusQuery,
} from '../../../common/redux/api/web-api'
import { canShowSmartScheduling } from '../../../common/domain/charging-schedules'
import { ChargePointCapiAuraControlWidget } from '../../../lib/charge-point-capi-aura-control-widget'
import { ChargePointCapiHaloControlWidget } from '../../../lib/charge-point-capi-halo-control-widget'
import { ChargePointCapiScheduledChargingWidget } from '../../../lib/charge-point-capi-scheduled-charging-widget'
import { ChargePointDawnControlWidget } from '../../../lib/charge-point-dawn-control-widget'
import { ChargePointOcppAuraControlWidget } from '../../../lib/charge-point-ocpp-aura-control-widget'
import { ChargePointOcppHaloControlWidget } from '../../../lib/charge-point-ocpp-halo-control-widget'
import ChargePointExpandOptions from '../../../lib/chargePointExpandOptions'
import { Header } from '../../../lib/header'
import isAura from '../../../lib/isAura'
import isDawn from '../../../lib/isDawn'
import isHalo from '../../../lib/isHalo'
import { WidgetLoadingState } from '../../../lib/widget-loading-state'
import { ChargePointScheduleDetailsWidget } from '../../../ui/chargePointScheduleDetailsWidget'
import isLuna from '../../../lib/isLuna'
import { ChargePointLunaControlWidget } from '../../../lib/charge-point-luna-control-widget'
import { ChargePointStatusWidget } from '../../../common/components/charge-point-status-widget'
import { CHARGE_POINT_REFRESH_TIMEOUT } from '../../../common/constants/ChargePointConstants'
import { notifyError } from '../../../common/features/notificationsManager/Notify'
import useText from '../../../common/hooks/useText'
import { useSessionUser } from '../../../common/hooks/useSessionUser'

import { ServicePartnerAccessWidget } from './service-partner-access-widget'

const Container = styled.div`
    margin-top: 40px;
`

const ChargePointContainer = styled.div`
    margin-top: 40px;
    display: flex;

    @media screen and (max-width: 990px) {
        flex-direction: column;
    }
`

const TabList = styled.div`
    display: flex;
    padding: 0 15px;
`

const ShimmerTab = styled.div`
    width: 100px;
    height: 20px;
    border-radius: 4px;
`

const Tab = styled.div<{ selected: boolean }>`
    border: 1px solid ${({ theme }) => theme.brandColors.CA_Light_Gray};
    border-radius: 6px 6px 0 0;
    border-bottom: none;
    padding: 10px 20px;
    background-color: ${({ theme, selected }) =>
        selected ? theme.font.lightestColor : theme.brandColors.CA_Light_Gray};

    font-weight: var(--font-weight-light);
    &:hover {
        cursor: pointer;
    }
`

export const UserAppRemoteControlPage = memo(function UserAppRemoteControlPage(): React.ReactElement {
    const text = useText()
    const user = useSessionUser()
    const [selectedChargePointId, setSelectedChargePointId] = useState<string>()

    const [getGCData, { data: chargePointStatus }] = useLazyGreenChargingChargePointStatusQuery()

    const {
        data: chargePoints,
        isSuccess,
        isLoading,
    } = useUserGetOwnedChargePointsQuery(
        {
            expand: `${ChargePointExpandOptions.OcppConfig},${ChargePointExpandOptions.TopChargingLimitation}`,
        },
        { pollingInterval: CHARGE_POINT_REFRESH_TIMEOUT }
    )

    const selectedChargePoint =
        chargePoints?.find((chargePoint) => chargePoint.id === selectedChargePointId) ?? chargePoints?.[0]
    const canGrantServicePartnerAccess = !!(
        selectedChargePoint?.companyId && user.roles.some((role) => role.companyId === selectedChargePoint.companyId)
    )

    const getGreenChargingData = async (chargePointId: string) => {
        await getGCData({ chargePointId })
            .unwrap()
            .catch(() => notifyError(text('msgFailedToGetGCData')))
    }

    const onTabClick = async (chargePoint: ChargePoint) => {
        setSelectedChargePointId(chargePoint.id)
        await getGreenChargingData(chargePoint.id)
    }

    /*************************************/

    return (
        <div className="page-layout-container">
            <Header code="lblRemoteControlPage" size="lg" data-testid="header" loading={isLoading} />
            {isSuccess && chargePoints ? (
                <Container>
                    <TabList>
                        {chargePoints?.map((c) => (
                            <Tab
                                selected={selectedChargePoint?.id === c.id}
                                onClick={() => onTabClick(c)}
                                key={c.id}
                                data-testid={c.id}
                            >
                                {c.name}
                            </Tab>
                        ))}
                    </TabList>

                    {selectedChargePoint && (
                        <ChargePointContainer>
                            <div className="col-xs-12 col-md-5">
                                {isHalo(selectedChargePoint) && selectedChargePoint.ocppServerMode !== 'Internal' && (
                                    <ChargePointCapiHaloControlWidget
                                        chargePoint={selectedChargePoint}
                                        isReadOnly={selectedChargePoint.ownerReadOnly}
                                    />
                                )}
                                {isHalo(selectedChargePoint) && selectedChargePoint.ocppServerMode === 'Internal' && (
                                    <ChargePointOcppHaloControlWidget
                                        chargePoint={selectedChargePoint}
                                        isReadOnly={selectedChargePoint.ownerReadOnly}
                                    />
                                )}
                                {isAura(selectedChargePoint) && selectedChargePoint.ocppServerMode !== 'Internal' && (
                                    <ChargePointCapiAuraControlWidget
                                        chargePoint={selectedChargePoint}
                                        isReadOnly={selectedChargePoint.ownerReadOnly}
                                        userId={user?.id}
                                    />
                                )}
                                {isAura(selectedChargePoint) && selectedChargePoint.ocppServerMode === 'Internal' && (
                                    <ChargePointOcppAuraControlWidget
                                        userId={user?.id}
                                        chargePoint={selectedChargePoint}
                                        isReadOnly={selectedChargePoint.ownerReadOnly}
                                    />
                                )}
                                {isDawn(selectedChargePoint) && (
                                    <ChargePointDawnControlWidget
                                        chargePoint={selectedChargePoint}
                                        isReadOnly={selectedChargePoint.ownerReadOnly}
                                    />
                                )}
                                {isLuna(selectedChargePoint) && (
                                    <ChargePointLunaControlWidget
                                        chargePoint={selectedChargePoint}
                                        isReadOnly={selectedChargePoint.ownerReadOnly}
                                    />
                                )}
                            </div>
                            <div className="col-xs-12 col-md-5">
                                <ChargePointStatusWidget chargePoint={selectedChargePoint} />
                                <ServicePartnerAccessWidget
                                    key={selectedChargePoint.id}
                                    chargePointId={selectedChargePoint.id}
                                    isAccessAvailable={canGrantServicePartnerAccess}
                                />
                            </div>
                            <div className="col-xs-12 col-md-5">
                                {!isDawn(selectedChargePoint) && selectedChargePoint.ocppServerMode !== 'Internal' && (
                                    <ChargePointCapiScheduledChargingWidget
                                        chargePoint={selectedChargePoint}
                                        isGreenChargingEnabled={chargePointStatus?.greenChargingStatus?.greenChargingOn}
                                    />
                                )}
                                {canShowSmartScheduling(selectedChargePoint, user?.isSuperAdmin) && (
                                    <ChargePointScheduleDetailsWidget
                                        chargePoint={selectedChargePoint}
                                        isGreenChargingEnabled={chargePointStatus?.greenChargingStatus?.greenChargingOn}
                                    />
                                )}
                            </div>
                        </ChargePointContainer>
                    )}
                </Container>
            ) : null}
            {isLoading && (
                <Container>
                    <TabList>
                        <Tab selected={true}>
                            <ShimmerTab className="shimmer-container"></ShimmerTab>
                        </Tab>
                    </TabList>
                    <ChargePointContainer>
                        <div className="col-xs-12 col-md-5">
                            <WidgetLoadingState height="285px" />
                        </div>
                        <div className="col-xs-12 col-md-5">
                            <WidgetLoadingState height="285px" />
                        </div>
                    </ChargePointContainer>
                </Container>
            )}
        </div>
    )
})
